<template>
	<Transition name="fade">
		<div v-if="show" class="game-id-input-container">
			<p class="id-heading">{{ `Enter a ${promptText}` }}<br><span class="id-sub">ESC to Cancel</span></p>
			<div class="game-id-input-box">
				<!-- Wrapper for absolute layout tracking -->
				<div class="input-timer-wrapper">
					<input
							ref="inputField"
							v-model="gameId"
							class="game-id-input"
							type="text"
							spellcheck="false"
							:placeholder="promptText ? promptText + ' ⏎' : 'ID ⏎'"
							@keyup.enter="confirmInput"
							@keydown.esc="cancelAction"
							autofocus
					/>
					
					<!-- Timer bar -->
					<div class="timer-container">
						<div class="timer-bar" :style="{ width: `${timerProgress}%` }"></div>
					</div>
					
					<!-- Return Home Button -->
					<Transition name="fade">
						<button
								v-if="isMouseActive"
								class="action-button home-button"
								@click="returnHome"
								ref="homeButton">
							<IconHome class="icon"/>
						</button>
					</Transition>
				</div>
				
				<div class="prompt">{{ promptMessage }}</div>
			</div>
		</div>
	</Transition>
</template>

<script setup>
import {ref, watch, onMounted, onUnmounted} from 'vue';
import {setActiveContext} from '@/services/keyboard.js';
import {params} from "@/systemInfo.js";
import {showNotification} from "@/services/notification.js";
import IconHome from "@/components/icons/IconHome.vue";

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    promptText: {
        type: String,
        required: true
    },
    timerDuration: {
        type: Number,
        default: params.gameIdInputTime || 120
    },
    validationState: {
        type: String,
        default: 'idle' // 'idle', 'validating', 'invalid', 'error'
    },
    errorMessage: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['confirm', 'cancel', 'show-return-home-prompt', 'validation-timeout', 'retry-validation', 'timer-expired']);
const gameId = ref('');
const inputField = ref(null);
const timerProgress = ref(100);
const promptMessage = ref('');
const isMouseActive = ref(false)
let timerInterval = null;

// Confirm input action
const confirmInput = () => {
    if (gameId.value.trim()) {
        clearInterval(timerInterval);
        emit('confirm', gameId.value.trim());
        // Update UI to show validating state
        promptMessage.value = `Validating ${props.promptText} ${gameId.value.trim().toUpperCase()}...`;
        // Disable the input field during validation
        if (inputField.value) {
            inputField.value.disabled = true;
        }
    }
};

const handleValidationResult = (result, message = '') => {
    if (result === 'valid') {
        // Valid ID - component will be hidden by parent
        clearInterval(timerInterval);
    } else if (result === 'invalid' || result === 'error') {
        // Invalid ID or error - clear input and show message
        gameId.value = '';
        promptMessage.value = message || (result === 'invalid'
            ? `Invalid ${props.promptText} or game has not started yet`
            : `Sorry, there was an error validating ${props.promptText}`);

        // Re-enable input
        if (inputField.value) {
            inputField.value.disabled = false;
            inputField.value.focus();
        }

        // Restart timer
        startTimer();
    }
};

const retryValidation = () => {
    gameId.value = '';
    promptMessage.value = props.promptText;
    if (inputField.value) {
        inputField.value.disabled = false;
        inputField.value.focus();
    }
    startTimer();
    emit('retry-validation');
};

// Cancel action
const cancelAction = () => {
    console.log("game ID cancel action")
    clearInterval(timerInterval);
    // emit('cancel');
	emit('show-return-home-prompt');
};

// Start the timer
const startTimer = () => {
    // Reset timer
    timerProgress.value = 100;
    clearInterval(timerInterval);

    // Start countdown
    const startTime = Date.now();
    timerInterval = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        timerProgress.value = 100 - (elapsed / props.timerDuration) * 100;

        if (timerProgress.value <= 0) {
            showNotification(`${props.promptText} not received... \nExiting`)
            clearInterval(timerInterval);
            emit('timer-expired');
        }
    }, 1000);
};

const updatePromptMessage = (message) => {
    promptMessage.value = message;
};

const returnHome = () => {
	emit('show-return-home-prompt');
};

const handleFirstMouseMove = () => {
	isMouseActive.value = true;
	window.removeEventListener('mousemove', handleFirstMouseMove);
};

// Watch for show changes
watch(
    () => props.show,
    (newValue) => {
        if (newValue) {
            promptMessage.value = 'ESC TO CANCEL';
            gameId.value = '';

            // Focus the input field
            setTimeout(() => {
                if (inputField.value) {
                    inputField.value.focus();
                }

                // Start the timer
                startTimer();
            }, 50);
        } else {
            // Input is hiding
            clearInterval(timerInterval);
        }
    }, {immediate: true}
);

watch(
    () => props.validationState,
    (newState) => {
        if (newState === 'validating') {
            // Disable input during validation
            if (inputField.value) {
                inputField.value.disabled = true;
                promptMessage.value = `Validating ${props.promptText} ${gameId.value.toUpperCase()}...`;
            }
            // Pause the timer during validation
            clearInterval(timerInterval);
        } else if (newState === 'invalid') {
            handleValidationResult('invalid', props.errorMessage);
        } else if (newState === 'error') {
            handleValidationResult('error', props.errorMessage);
        } else if (newState === 'idle') {
            retryValidation();
        }
    }
);

watch(gameId, () => {
    // Only restart timer if input is showing
    if (props.show) {
        startTimer();
    }
});

onMounted( () => {
	window.addEventListener('mousemove', handleFirstMouseMove);
})

// Clean up on unmount
onUnmounted(() => {
    clearInterval(timerInterval);
	window.removeEventListener('mousemove', handleFirstMouseMove);
});

// Expose methods to parent components
defineExpose({
    confirmInput,
    cancelAction,
    updatePromptMessage,
    handleValidationResult,
    retryValidation
});
</script>

<style scoped>
.id-heading {
	position: absolute;
	height: calc(var(--cqh) * 100);
	width: calc(var(--cqw) * 100);
	top: var(--spacing-2xl);
	font-size: var(--font-size-xl);
	font-weight: var(--font-weight-medium);
	margin-bottom: var(--spacing-md);
	color: var(--color-text);
	text-transform: uppercase;
	text-align: center;
	line-height: 2.8rem;
}

.id-sub {
	font-size: var(--font-size-md);
	font-weight: var(--font-weight-light);
	color: var(--color-text-secondary);
	margin-top: var(--spacing-lg);
}

.game-id-input-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: var(--spacing-xl);
    display: flex;
    justify-content: center;
    align-items: flex-end;
    z-index: 103;
    background-color: transparent;
}

.input-timer-wrapper {
	position: relative;
	width: 45%;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.game-id-input-box {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 80%;
    /* max-width: 33vw; */
    text-align: center;
    padding: var(--spacing-md);
    border-radius: var(--border-radius-md);
    /* background-color: rgba(0, 0, 0, 0.7); */
    /* backdrop-filter: blur(5px); */
}

.game-id-input {
    width: 100%;
    padding: var(--spacing-md);
    font-size: var(--font-size-lg);
    border-radius: var(--border-radius-sm);
    border: none;
    background-color: var(--color-popup-element-light);
    color: var(--color-text);
    text-align: center;
    margin-bottom: var(--spacing-md);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    text-transform: uppercase;
}

.timer-container {
    width: 100%;
    height: var(--font-size-sm);
    background-color: var(--color-border);
    border-radius: var(--border-radius-full);
    overflow: hidden;
}

.timer-bar {
    height: 100%;
    min-width: 3%;
    border-radius: var(--border-radius-full);
    background: var(--gradient-primary);
    transition: width var(--transition-linear);
}

.prompt {
    /* width: 100vw; */
    /* text-align: center; */
    margin-top: var(--spacing-md);
    font-size: var(--font-size-md);
    min-height: var(--font-size-lg);
}

.action-button {
	position: absolute;
	right: calc(100% + var(--spacing-md));
	top: 0;
	bottom: 0;
	aspect-ratio: 1 / 1;
	background: var(--color-popup-element-light);
	border: none;
	border-radius: var(--border-radius-md);
	padding: var(--spacing-md);
	margin-bottom: 0;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background-color 0.2s, transform 0.2s;
	backdrop-filter: blur(5px);
	z-index: 1001;
}

.action-button.home-button .icon {
	width: 100%;
	height: 100%;
}

/* Transition classes */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

</style>
